import { useMemo } from "react";
import clsx from "clsx";
import { DatePickerCalendarRoot } from "./DatePickerCalendar.styles";
import DatePickerCalendarHeader from "./DatePickerCalendarHeader/DatePickerCalendarHeader";
import { AnyDate, DatePickerCalendarProps } from "./DatePickerCalendar.types";
import { DATE_PICKER_CALENDAR_LOCALES } from "./DatePickerCalendarLocales";
import "react-day-picker/dist/style.css";
import { useControlled } from "@commonlibs";
import isValidDate from "./utils/isValidDate";
import DatePickerFooter from "./DatePickerFooter/DatePickerFooter";
import { default as config } from "common/config/config";

function DatePickerCalendar({
    className,
    locale = "es",
    fromDate = new Date(config.startFilteringDate),
    toDate = new Date(),
    mode = "single",
    onChange,
    value,
    defaultValue,
    defaultMonth: defaultMonthProp,
    onReset,
    onApply,
    showFooter = true,
    ...rest
}: DatePickerCalendarProps) {
    //@ts-ignore - avoid breaking the application
    const [selected, setSelected] = useControlled(defaultValue, value, onChange);
    const finalLocale = useMemo(() => DATE_PICKER_CALENDAR_LOCALES[locale], [locale]);

    function getSelectedDate(date: AnyDate | undefined) {
        setSelected(date as Date);
    }

    const defaultMonth = useMemo(() => {
        const finalDefault = defaultMonthProp || selected || new Date();
        if (isValidDate(finalDefault)) return finalDefault;
        return new Date();
    }, [defaultMonthProp, selected]);

    function handleReset() {
        setSelected(undefined); // skipcq: JS-W1042
        onReset?.();
    }

    function handleApply() {
        //@ts-ignore - avoid breaking the application
        onApply?.(selected);
    }

    return (
        // @ts-ignore - avoid breaking the application
        <DatePickerCalendarRoot
            selected={selected}
            onSelect={getSelectedDate}
            mode={mode}
            className={clsx("date-picker-calendar", className)}
            locale={finalLocale}
            defaultMonth={defaultMonth}
            modifiersClassNames={{
                today: "my-today",
                selected: "my-selected",
            }}
            components={{
                Caption: (props) => <DatePickerCalendarHeader locale={finalLocale} {...props} />,
            }}
            fromDate={fromDate}
            toDate={toDate}
            footer={showFooter && <DatePickerFooter handleReset={handleReset} handleApply={handleApply} />}
            {...rest}
        />
    );
}

export default DatePickerCalendar;
